import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { gaEvent } from "../lib/analytics";
import { ScoreGauge } from "../components/ScoreGauge";
import { CategoryBars } from "../components/CategoryBars";
import { usePublicConfig } from "../lib/useConfig";
import { Loader2, Download, CheckCircle2, XCircle, ArrowRight, FileText, Compass, MessageSquare, ListChecks, BookOpen, AlertTriangle, TrendingUp, RefreshCw, Clock } from "lucide-react";

/* -------- Payment success -------- */
export function PaymentSuccess() {
  const [params] = useSearchParams();
  let sessionId = params.get("session_id") || params.get("reference") || params.get("trxref");
  if (!sessionId) { try { sessionId = localStorage.getItem("fl_last_session"); } catch (e) { /* ignore */ } }
  const [status, setStatus] = useState("checking");
  const [assessmentId, setAssessmentId] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (stop) return;
        setAssessmentId(data.assessment_id);
        if (data.payment_status === "paid") {
          gaEvent("payment_completed", { transaction_id: sessionId, assessment_id: data.assessment_id, value: data.amount, currency: (data.currency || "").toUpperCase(), tier: data.tier });
          setStatus("paid"); return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch (e) { /* keep trying */ }
      tries.current += 1;
      if (tries.current > 20) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center">
      {status === "checking" && (<><Loader2 className="animate-spin mx-auto text-gold mb-4" size={40} /><h1 className="text-2xl font-bold text-navy">Confirming your payment…</h1><p className="mt-2 text-muted-foreground">This only takes a moment.</p></>)}
      {status === "paid" && (<><CheckCircle2 className="mx-auto text-forest mb-4" size={48} /><h1 className="text-3xl font-bold text-ink">You're all set</h1><p className="mt-3 text-ink/70">Thanks — we're putting your full report together now. It'll be ready in about a minute.</p>{assessmentId && <Link to={`/report/${assessmentId}`} data-testid="view-report-btn" className="mt-8 btn-gold inline-flex items-center gap-2 font-semibold px-8 py-4 rounded-[3px]">See my report <ArrowRight size={18} /></Link>}</>)}
      {(status === "failed") && (<><XCircle className="mx-auto text-red-600 mb-4" size={48} /><h1 className="text-2xl font-bold text-navy">Payment was not completed</h1><p className="mt-3 text-charcoal/70">No charge was made. You can try again anytime.</p><Link to="/dashboard" className="mt-8 inline-block text-navy font-semibold">Back to dashboard</Link></>)}
      {(status === "timeout" || status === "error") && (<><h1 className="text-2xl font-bold text-navy">Still processing</h1><p className="mt-3 text-charcoal/70">Your payment may still be confirming. Check your dashboard shortly.</p><Link to="/dashboard" className="mt-8 inline-block text-navy font-semibold">Go to dashboard</Link></>)}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center">
      <XCircle className="mx-auto text-copper mb-4" size={48} />
      <h1 className="text-2xl font-bold text-navy">Checkout canceled</h1>
      <p className="mt-3 text-charcoal/70">No charge was made. Your assessment and free snapshot are saved.</p>
      <Link to="/dashboard" className="mt-8 inline-block btn-gold font-semibold px-8 py-3.5 rounded-[3px]">Back to dashboard</Link>
    </div>
  );
}

/* -------- Full Report -------- */
const Section = ({ icon: Icon, title, children, id }) => (
  <section className="py-10 border-t border-line first:border-0" data-testid={`report-section-${id}`}>
    <div className="flex items-center gap-2.5 mb-5">
      <Icon className="text-gold" size={20} />
      <h2 className="text-xl font-bold text-navy font-display">{title}</h2>
    </div>
    {children}
  </section>
);

const CAT_LABELS = { story: "Story Readiness", evidence: "Evidence Readiness", revenue: "Revenue Readiness", relationship: "Relationship Readiness", infrastructure: "Infrastructure Readiness" };

export default function PaidReport() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isSample = params.get("sample") === "1";
  const config = usePublicConfig();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const tries = useRef(0);
  const viewedRef = useRef(false);
  const POLL_MS = 3000;
  const MAX_TRIES = 100; // ~5 minutes of polling

  useEffect(() => {
    let stop = false;
    tries.current = 0;
    setTimedOut(false);
    const load = async () => {
      try {
        const { data } = await api.get(`/assessments/${id}/report`);
        if (stop) return;
        setData(data);
        if (data.report && data.report_status !== "generating" && !viewedRef.current) {
          viewedRef.current = true;
          gaEvent("report_viewed", { assessment_id: id, tier: data.purchased_tier || "full", sample: isSample });
        }
        if (data.report_status === "generating") {
          if (tries.current < MAX_TRIES) {
            tries.current += 1;
            setTimeout(load, POLL_MS);
          } else {
            setTimedOut(true);
          }
        }
      } catch (e) {
        if (stop) return;
        setError(formatApiError(e.response?.data?.detail) || "Unable to load report");
      }
    };
    load();
    return () => { stop = true; };
  }, [id, attempt]);

  const retryLoad = () => { setError(""); setData(null); setTimedOut(false); setAttempt((a) => a + 1); };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/assessments/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Fundability_Blueprint.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error("PDF download failed", e); } finally { setDownloading(false); }
  };

  if (error) return <div className="max-w-2xl mx-auto px-5 py-24 text-center text-muted-foreground">{error}</div>;
  if (!data) return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto text-gold" /></div>;

  const r = data.report || {};
  const isFocus = data.purchased_tier === "focus";
  const generating = data.report_status === "generating" || !data.report;
  const scores = data.scores || {};
  const profile = data.profile || {};

  if (generating) {
    if (timedOut) {
      return (
        <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="report-timeout">
          <Clock className="mx-auto text-clay mb-4" size={40} />
          <h1 className="text-2xl font-bold text-ink">This is taking a little longer than usual</h1>
          <p className="mt-3 text-ink/70">Your report is still being prepared. It'll open here automatically the moment it's ready — you can check again now, or head to your dashboard and come back shortly.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <button onClick={retryLoad} data-testid="report-retry-btn" className="btn-gold font-semibold px-6 py-3.5 rounded-[3px] inline-flex items-center gap-2">
              <RefreshCw size={16} /> Check again
            </button>
            <Link to="/dashboard" className="text-navy font-semibold">Go to dashboard</Link>
          </div>
        </div>
      );
    }
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="report-generating">
        <Loader2 className="animate-spin mx-auto text-clay mb-4" size={40} />
        <h1 className="text-2xl font-bold text-ink">Putting your report together…</h1>
        <p className="mt-3 text-ink/70">We're reading through your answers. This can take a couple of minutes — the page will update on its own as soon as it's ready.</p>
      </div>
    );
  }

  return (
    <div>
      {isSample && (
        <div className="bg-ochre/20 border-b border-ochre/40" data-testid="sample-banner">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-ink/80">
              <span className="font-semibold text-ink">This is a sample report</span> for a fictional organization, so you can see the full depth before you start.
            </p>
            <Link to="/assessment/new" data-testid="sample-start-own"
              className="btn-gold text-sm font-semibold px-5 py-2 rounded-[3px] whitespace-nowrap self-start">
              Check your own funding readiness
            </Link>
          </div>
        </div>
      )}
      {/* Cover */}
      <section className="bg-green text-cream relative grain overflow-hidden">
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <span className="text-ochre text-xs font-semibold tracking-[0.2em] uppercase">Your full report</span>
              <h1 className="mt-3 text-3xl sm:text-4xl font-bold">{profile.org_name || "Your Organization"}</h1>
              <p className="mt-2 text-cream/60 text-sm">Prepared {data.completed_at ? new Date(data.completed_at).toLocaleDateString() : "recently"}</p>
            </div>
            {!isFocus && (
              <button onClick={downloadPdf} disabled={downloading} data-testid="download-pdf"
                className="btn-gold font-semibold px-6 py-3.5 rounded-[3px] flex items-center gap-2 shrink-0">
                {downloading ? <Loader2 className="animate-spin" size={18} /> : <><Download size={18} /> Download PDF</>}
              </button>
            )}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-8 items-center bg-offwhite rounded-md p-8 text-ink">
            <ScoreGauge score={scores.overall_score} label={data.readiness_category} size={220} />
            <CategoryBars scores={scores.category_scores} />
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
        <Section icon={FileText} title="Executive Summary" id="summary">
          <p className="text-charcoal/85 leading-relaxed">{r.executive_summary}</p>
        </Section>

        <Section icon={TrendingUp} title="Where You Stand" id="overall">
          <p className="text-lg font-display font-semibold text-navy">{scores.overall_score}/100 — {data.readiness_category}</p>
          <p className="mt-2 text-charcoal/85 leading-relaxed">{r.overall_explanation}</p>
        </Section>

        <Section icon={BookOpen} title="A Closer Look at Each Area" id="categories">
          <div className="space-y-5">
            {Object.entries(r.category_analysis || {}).map(([k, v]) => (
              <div key={k}>
                <p className="font-display font-semibold text-navy">{CAT_LABELS[k] || k} — {scores.category_scores?.[k]}/100</p>
                <p className="mt-1 text-charcoal/80 leading-relaxed text-sm">{v}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={TrendingUp} title="What's Working in Your Favor" id="strengths">
          <ul className="space-y-2.5">
            {(r.strongest_assets || []).map((s, i) => (
              <li key={i} className="flex gap-3 text-charcoal/85"><span className="w-1.5 h-1.5 bg-forest rotate-45 mt-2 shrink-0" />{s}</li>
            ))}
          </ul>
        </Section>

        <Section icon={AlertTriangle} title="What Needs Your Attention" id="vulnerabilities">
          <ul className="space-y-2.5">
            {(r.priority_vulnerabilities || []).map((s, i) => (
              <li key={i} className="flex gap-3 text-charcoal/85"><span className="w-1.5 h-1.5 bg-copper rotate-45 mt-2 shrink-0" />{s}</li>
            ))}
          </ul>
        </Section>

        <Section icon={Compass} title="Where You Can Raise Money" id="pathways">
          <div className="space-y-4">
            {(r.recommended_pathways || []).map((p, i) => (
              <div key={i} className="p-5 bg-white border border-line rounded-md">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-navy">{i + 1}. {p.pathway}</span>
                </div>
                <dl className="mt-3 space-y-1.5 text-sm">
                  {[["Why it fits", p.why_it_fits], ["What must be in place", p.what_must_be_in_place], ["First step", p.first_step], ["Time horizon", p.time_horizon], ["Potential challenges", p.challenges]].map(([k, v]) => (
                    <div key={k} className="flex gap-2"><dt className="font-semibold text-navy shrink-0 min-w-[130px]">{k}:</dt><dd className="text-charcoal/80">{v}</dd></div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          {(r.deprioritize_pathways || []).length > 0 && (
            <div className="mt-6">
              <h3 className="font-display font-semibold text-navy mb-2">Deprioritize for now</h3>
              <ul className="space-y-2 text-sm">
                {r.deprioritize_pathways.map((d, i) => (
                  <li key={i} className="text-charcoal/80"><span className="font-semibold text-navy">{d.pathway}:</span> {d.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </Section>

        {!isFocus && (<>
        <Section icon={BookOpen} title="Your Funding Story (a draft to build on)" id="story">
          <div className="p-6 bg-white border border-line rounded-md">
            <p className="text-charcoal/85 leading-relaxed font-serif-accent whitespace-pre-line">{r.funding_story}</p>
          </div>
        </Section>

        <Section icon={ListChecks} title="What Would Strengthen Your Case" id="missing">
          <div className="flex flex-wrap gap-2">
            {(r.missing_evidence || []).map((m, i) => (
              <span key={i} className="px-3 py-1.5 bg-copper/10 border border-copper/20 rounded-full text-sm text-charcoal/80">{m}</span>
            ))}
          </div>
        </Section>

        <Section icon={ListChecks} title="30-, 60-, and 90-Day Action Plan" id="action-plan">
          <div className="space-y-6">
            {[["30_day", "First 30 Days"], ["60_day", "Days 31–60"], ["90_day", "Days 61–90"]].map(([key, title]) => (
              (r.action_plan?.[key] || []).length > 0 && (
                <div key={key}>
                  <h3 className="font-display font-semibold text-gold mb-3 uppercase text-sm tracking-wider">{title}</h3>
                  <div className="space-y-3">
                    {r.action_plan[key].map((a, i) => (
                      <div key={i} className="p-4 bg-white border border-line rounded-md">
                        <p className="font-semibold text-navy">{a.action}</p>
                        <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-charcoal/70">
                          <span><b className="text-navy">Purpose:</b> {a.purpose}</span>
                          <span><b className="text-navy">Owner:</b> {a.owner}</span>
                          <span><b className="text-navy">Effort:</b> {a.effort}</span>
                          <span><b className="text-navy">Result:</b> {a.expected_result}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </Section>

        <Section icon={MessageSquare} title="Leadership & Board Discussion Guide" id="board">
          <ol className="space-y-2.5 list-decimal list-inside">
            {(r.board_discussion_questions || []).map((q, i) => (
              <li key={i} className="text-charcoal/85 leading-relaxed">{q}</li>
            ))}
          </ol>
        </Section>
        </>)}

        {isFocus && (
          <div className="my-10 p-7 bg-green text-cream rounded-md relative grain overflow-hidden text-center" data-testid="focus-upgrade">
            <div className="relative z-10 max-w-lg mx-auto">
              <p className="font-serif-accent text-xl text-ochre">Want the rest of the plan?</p>
              <p className="mt-1 text-cream/90">The full report adds your draft funding story, a 30/60/90-day action plan, a board discussion guide, a PDF, and email delivery.</p>
              <Link to={`/results/${id}`} className="mt-5 inline-block btn-gold font-semibold px-6 py-3 rounded-[3px]">Upgrade to the full report</Link>
            </div>
          </div>
        )}

        <div className="py-8 border-t border-line">
          <p className="text-xs text-muted-foreground leading-relaxed italic">{config?.disclaimer_report}</p>
        </div>
      </div>
    </div>
  );
}
